/** Demo history for ?mock=1: deterministic per bucket, busier on weekdays and in working hours. */
import { HISTORY_METRICS } from '@orgus/protocol';
import type { HistoryBucketSize, HistoryMetric, HistorySeriesResult, HistorySession, HistorySessionsResult } from '@orgus/protocol';

const HOUR = 3_600_000;
const DAY = 86_400_000;

const VALUES: Record<string, string[]> = {
  agent: ['claude-code', 'codex', 'gemini'],
  model: ['claude-opus-4-5', 'claude-sonnet-4-5', 'gpt-5-codex'],
  project: ['orgus', 'qawl', 'site'],
  machine: ['build-01', 'mac-studio', 'laptop-sv'],
  outcome: ['done', 'empty', 'gone'],
  result: ['ok', 'error'],
};
const ACTIONS: Partial<Record<HistoryMetric, string[]>> = {
  missions: ['mission.create', 'mission.start', 'mission.done', 'mission.fail'],
  approvals: ['approval.allow', 'approval.deny', 'approval.ask'],
  landings: ['landing.land', 'landing.reject'],
  incidents: ['watch.incident', 'security.alert'],
  audit: ['session', 'mission', 'approval', 'policy'],
};
/** Per hour, at a busy hour, per unit share. */
const SCALE: Partial<Record<HistoryMetric, number>> = {
  'sessions.started': 6,
  'sessions.ended': 6,
  turns: 140,
  toolCalls: 520,
  'tokens.input': 2_400_000,
  'tokens.output': 380_000,
  'tokens.cacheRead': 9_000_000,
  'cost.usd': 9,
  'machines.onlineMinutes': 60,
  'machines.online': 3,
  missions: 2,
  approvals: 7,
  landings: 3,
  incidents: 0.4,
  audit: 30,
  'wire.patches': 3_200,
  'wire.bytes': 4_200_000,
};
const SHARE = [0.62, 0.28, 0.1];

const noise = (n: number): number => {
  const x = Math.sin(n * 12.9898) * 43758.5453;
  return x - Math.floor(x);
};

/** How busy an hour is: weekdays, 9 to 19 local. */
const busy = (t: number): number => {
  const d = new Date(t);
  const wd = d.getDay() === 0 || d.getDay() === 6 ? 0.25 : 1;
  const h = d.getHours();
  const work = h >= 9 && h <= 19 ? 1 : h >= 7 && h <= 22 ? 0.4 : 0.08;
  return wd * work;
};

const groupsOf = (metric: HistoryMetric, dim: string | null): string[] => {
  if (!dim || !(HISTORY_METRICS[metric] as readonly string[]).includes(dim)) return [''];
  if (dim === 'action') return ACTIONS[metric] ?? ['other'];
  return VALUES[dim] ?? [''];
};

function hourValue(metric: HistoryMetric, t: number, gi: number, groups: number): number {
  const base = SCALE[metric] ?? 1;
  if (metric === 'machines.onlineMinutes') return Math.round(60 * (gi === 2 ? busy(t) : 0.92 + 0.08 * noise(t / HOUR + gi)));
  if (metric === 'machines.online') return gi === 0 ? Math.round(2 + busy(t)) : 0;
  const share = groups === 1 ? 1 : (SHARE[gi] ?? 0.05);
  const v = base * busy(t) * share * (0.55 + 0.9 * noise(t / HOUR + gi * 7 + metric.length));
  return metric === 'cost.usd' ? Math.round(v * 100) / 100 : Math.round(v);
}

export function mockSeries(from: number, to: number, metrics: readonly HistoryMetric[], groupBy: string | null, bucket: HistoryBucketSize): HistorySeriesResult {
  const step = bucket === 'hour' ? HOUR : DAY;
  // Days start at local midnight, as the hub's do (QA-77).
  const off = bucket === 'hour' ? 0 : -new Date(from).getTimezoneOffset() * 60_000;
  const start = Math.floor((from + off) / step) * step - off;
  const buckets: number[] = [];
  for (let t = start; t < to; t += step) buckets.push(t);
  // Demo history begins 75 days ago, so a 90 day range shows the partial state.
  const since = Date.now() - 75 * DAY;
  const series = metrics.flatMap((metric) => {
    const groups = groupsOf(metric, groupBy);
    return groups.map((group, gi) => ({
      metric,
      group,
      values: buckets.map((b) => {
        if (b + step < since) return 0;
        let v = 0;
        for (let h = b; h < Math.min(b + step, to); h += HOUR) v = metric === 'machines.online' ? Math.max(v, hourValue(metric, h, gi, groups.length)) : v + hourValue(metric, h, gi, groups.length);
        return Math.round(v * 100) / 100;
      }),
    }));
  });
  return { from, to, bucket, groupBy, buckets, series, since: new Date(since).toISOString().slice(0, 10) };
}

const TITLES = ['Fix the flaky landing test', 'Add date ranges to Spend', 'Review the node trust patch', 'Ship the status page', 'Refactor the costs ledger', 'Write the history ADR', 'Port missions to the control plane', 'Tidy the design tokens'];

export function mockSessions(from: number, to: number, q: string): HistorySessionsResult {
  const out: HistorySession[] = [];
  for (let t = Math.floor(from / HOUR) * HOUR; t < to; t += HOUR) {
    const n = Math.round(4 * busy(t) * noise(t / HOUR));
    for (let i = 0; i < n; i++) {
      const r = noise(t / HOUR + i * 3.1);
      const start = t + Math.round(r * HOUR * 0.8);
      const end = start + Math.round((10 + 140 * noise(start)) * 60_000);
      // Only sessions that have ended by now.
      if (end >= Math.min(to, Date.now())) continue;
      const mi = Math.floor(noise(start + 1) * 3);
      const turns = Math.round(40 * noise(start + 2));
      const id = `ses_demo_${Math.round(start / 1000).toString(36)}${i}`;
      const asked = noise(start + 5) > 0.6 ? [start + Math.round((end - start) * 0.5)] : undefined;
      out.push({
        id,
        machineId: `m_${VALUES.machine![mi]}`,
        machine: VALUES.machine![mi]!,
        projectId: `p_${VALUES.project![mi]}`,
        project: VALUES.project![mi]!,
        title: TITLES[Math.floor(noise(start + 3) * TITLES.length)]!,
        agent: noise(start + 4) > 0.75 ? 'codex' : 'claude-code',
        model: noise(start + 4) > 0.75 ? 'gpt-5-codex' : 'claude-opus-4-5',
        origin: noise(start + 6) > 0.5 ? 'orgus' : 'interactive',
        startedAt: start,
        endedAt: end,
        turns,
        toolCalls: turns * 4,
        tokens: { input: turns * 52_000, output: turns * 6_000, cacheRead: turns * 210_000 },
        costUsd: Math.round(turns * 0.31 * 100) / 100,
        outcome: turns === 0 ? 'empty' : noise(start + 7) > 0.92 ? 'gone' : 'done',
        ...(asked ? { asked } : {}),
      });
    }
  }
  const needle = q.trim().toLowerCase();
  const list = out
    .filter((s) => s.endedAt >= from && s.endedAt < to && (!needle || [s.title, s.id, s.project, s.model, s.machine].some((x) => x?.toLowerCase().includes(needle))))
    .sort((a, b) => b.endedAt - a.endedAt);
  return { from, to, sessions: list.slice(0, 100), next: list.length > 100 ? 'demo:100' : null, unreadable: [] };
}
